// The frame loop mirroring the daemon's holder (sdk ADR-012, sdk ADR-018, la ADR-010).
import type { IdentityController } from './controller.js';
import type {
  CredentialMaterial,
  HolderEvent,
  SessionVerdict,
} from '../auth/authState.js';
import { credentialFingerprint } from '../auth/decodeSessionJwt.js';
import { expiresAtOfJwt } from './material.js';
import { emitLifecycleEvent } from '../lifecycle/observability.js';
import type {
  ShogunChopAuthChangedData,
  ShogunChopDegradedReason,
} from '../types/shogunchop.js';

/** What the arm does about an accepted frame. */
export type MirrorAction =
  /** Fetch the daemon's bearer from `/auth/info` and acquire it. */
  | { kind: 'acquire' }
  /** Apply this event directly; no material needed. */
  | { kind: 'apply'; event: HolderEvent };

function degradedVerdict(reason: ShogunChopDegradedReason): SessionVerdict {
  switch (reason) {
    case 'expired':
      return { verdict: 'expired' };
    case 'unconfirmed':
      return { verdict: 'unknown', cause: 'server_error' };
    default:
      throw new Error(
        `mirrorAction: degraded frame carries no recognised degraded_reason (${String(reason)})`,
      );
  }
}

// The one wire→union translation site (sdk ADR-012).
export function mirrorAction(data: ShogunChopAuthChangedData, at: number): MirrorAction {
  if (!data?.phase) {
    throw new Error('mirrorAction: auth:changed frame carries no phase');
  }
  switch (data.phase) {
    case 'usable':
      return { kind: 'acquire' };
    case 'unactivated':
      return {
        kind: 'apply',
        event: {
          type: 'VerdictObserved',
          verdict: { verdict: 'not_activated', cause: 'missing_account_claim' },
          at,
        },
      };
    case 'degraded':
      return {
        kind: 'apply',
        event: {
          type: 'VerdictObserved',
          verdict: degradedVerdict(data.degraded_reason),
          at,
        },
      };
    case 'acquiring':
      return {
        kind: 'apply',
        event: { type: 'SignInStarted', at, source: 'browser_bounce' },
      };
    case 'no_credential':
      // The daemon is the holder, so its clear is machine-wide (sdk ADR-015).
      return { kind: 'apply', event: { type: 'RemoteClear', reason: data.reason } };
  }
}

/** What a daemon-arm renewal round trip resolved to. */
export type DaemonRenewalOutcome =
  | { kind: 'unchanged' }
  | { kind: 'renewed'; event: HolderEvent };

/** (sdk ADR-018) */
export function daemonRenewal(
  heldJwt: string,
  fetched: CredentialMaterial | null,
): DaemonRenewalOutcome {
  if (fetched === null) return { kind: 'unchanged' };
  if (credentialFingerprint(fetched.session_jwt) === credentialFingerprint(heldJwt)) {
    return { kind: 'unchanged' };
  }
  const expires_at = expiresAtOfJwt(fetched.session_jwt);
  if (expires_at === null) {
    throw new Error(
      'daemonRenewal: the daemon returned a session JWT with no exp claim — nothing to hold it by',
    );
  }
  return {
    kind: 'renewed',
    event: { type: 'CredentialRenewed', material: fetched, expires_at },
  };
}

export interface DaemonFrame {
  event: string;
  data?: unknown;
  seq?: unknown;
}

export interface DaemonArmHandle {
  /** Release the shared `/events` reference (sdk ADR-006). */
  release(): void;
}

/** The one route from a daemon frame to the holder (sdk ADR-013). */
export function applyDaemonFrame(
  controller: IdentityController,
  data: ShogunChopAuthChangedData,
): Promise<void> {
  const action = mirrorAction(data, Date.now());
  if (action.kind === 'acquire') {
    // Fingerprint-gated replay suppression (sdk ADR-018).
    return controller.acquireFromDaemon();
  }
  if (action.event.type === 'RemoteClear') {
    // The daemon's clear is machine-wide (sdk ADR-015).
    return controller.observeDaemonClear(action.event.reason);
  }
  return controller.applyMirror(action.event);
}

/** Mirror `/events` into the holder from `sinceSeq` up (sdk ADR-013). */
export function startDaemonArm(
  controller: IdentityController,
  events: {
    on(handler: (event: DaemonFrame) => void): () => void;
    release(): void;
  },
  sinceSeq = -1,
): DaemonArmHandle {
  let highestSeq = sinceSeq;
  let chain: Promise<void> = Promise.resolve();

  events.on((frame) => {
    if (frame.event !== 'auth:changed') return;
    const data = frame.data as ShogunChopAuthChangedData | undefined;
    if (!data?.phase) {
      // Named before it is dropped (.claude/rules/no-fallbacks.md, sdk ADR-027).
      emitLifecycleEvent('identity.frame_malformed', { realm: controller.armName, reason: 'no_phase' });
      return;
    }
    const seq = frame.seq;
    if (typeof seq !== 'number' || !Number.isFinite(seq)) {
      emitLifecycleEvent('identity.frame_malformed', { realm: controller.armName, reason: 'no_seq' });
      return;
    }
    if (seq <= highestSeq) return;
    highestSeq = seq;
    chain = chain
      .then(() => applyDaemonFrame(controller, data))
      // Rethrown off the chain, so one failed frame surfaces without refusing every later one (no-fallbacks.md).
      .catch((cause: unknown) => {
        setTimeout(() => {
          throw cause;
        }, 0);
      });
  });
  return { release: () => events.release() };
}
